import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import Integration from "@/model/interface/Integration";
import IntegrationSyncToggle from "@/components/pages/integrations/IntegrationSyncToggle";
import {
  useConnectReiSiftMutation,
  useUpdateIntegrationSettingsMutation,
  useUpdateIntegrationSyncSettingsMutation,
  useSyncIntegrationMutation,
} from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  integration: Integration;
}

export default function ReiSiftSettings({ integration }: Props) {
  const [connect, { isLoading: isConnecting }] = useConnectReiSiftMutation();
  const [updateSettings] = useUpdateIntegrationSettingsMutation();
  const [updateSyncSettings] = useUpdateIntegrationSyncSettingsMutation();
  const [syncIntegration, { isLoading: isSyncing }] = useSyncIntegrationMutation();
  const [apiKey, setApiKey] = useState("");

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      toast.error("API key is required");
      return;
    }
    try {
      await connect({ integrationId: integration.id, apiKey }).unwrap();
      toast.success("REISift connected");
      setApiKey("");
    } catch {
      toast.error("Failed to connect");
    }
  };

  const updateSetting = async (key: string, value: unknown) => {
    try {
      await updateSettings({ id: integration.id, settings: { [key]: value } }).unwrap();
    } catch {
      toast.error("Failed to update setting");
    }
  };

  const updateSyncDirection = async (value: string) => {
    try {
      await updateSyncSettings({
        id: integration.id,
        syncEnabled: integration.syncEnabled !== false,
        syncDirection: value,
      }).unwrap();
    } catch {
      toast.error("Failed to update sync direction");
    }
  };

  const handleSync = async () => {
    try {
      await syncIntegration(integration).unwrap();
      toast.success("Contact sync completed");
    } catch {
      toast.error("Sync failed");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Connection</CardTitle>
          <CardDescription>Connect with your REISift API key. Leads sync both ways.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <IntegrationSyncToggle integration={integration} />

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Status</p>
              <p className="text-sm text-muted-foreground">
                {integration.lastSyncAt
                  ? `Last synced ${new Date(integration.lastSyncAt).toLocaleString()}`
                  : "Never synced"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={integration.status === "active" ? "default" : "secondary"}>{integration.status}</Badge>
              <Button
                onClick={handleSync}
                disabled={isSyncing || integration.syncEnabled === false || integration.status !== "active"}
                size="sm"
              >
                {isSyncing ? "Syncing..." : "Sync Now"}
              </Button>
            </div>
          </div>

          <div className="space-y-2 max-w-sm">
            <Label htmlFor="apiKey">API Key</Label>
            <Input
              id="apiKey"
              type="password"
              placeholder={integration.status === "active" ? "Enter a new key to replace the existing one" : "API key"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
          <Button onClick={handleConnect} disabled={isConnecting}>
            {isConnecting ? "Connecting..." : integration.status === "active" ? "Update Connection" : "Connect"}
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Sync Direction</p>
              <p className="text-sm text-muted-foreground">Controls whether leads are pulled, pushed, or both</p>
            </div>
            <Select
              value={integration.syncDirection || "BIDIRECTIONAL"}
              onValueChange={(value) => updateSyncDirection(value)}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BIDIRECTIONAL">Bidirectional</SelectItem>
                <SelectItem value="PULL_ONLY">Pull Only</SelectItem>
                <SelectItem value="PUSH_ONLY">Push Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
